import { useMemo } from "react";
import {
  FaBatteryHalf,
  FaInfoCircle,
  FaLightbulb,
  FaSun,
  FaChartLine,
} from "react-icons/fa";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import InstallationMetricSnapshot from "./installation/InstallationMetricSnapshot";
import InstallationStateSupportSection from "./installation/InstallationStateSupportSection";
import InstallationSubpageLayout from "./installation/InstallationSubpageLayout";
import { useInstallationCamera } from "./installation/useInstallationCamera";

const INSTALLATION_BATTERY_TARGET_PERCENT = 75;

function getBatteryVisual(level) {
  if (level >= 75) {
    return { tone: "ok", gaugeColor: "#00c4c3" };
  }
  if (level >= 50) {
    return { tone: "warn", gaugeColor: "#f8aa00" };
  }
  return { tone: "low", gaugeColor: "#e5193c" };
}

const AppInstallationBatteryDetail = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const { loading, batteryLevel: loadedBatteryLevel } =
    useInstallationCamera(id);

  const batteryLevel = useMemo(() => {
    const fromRoute = location.state?.batteryLevel;
    if (
      fromRoute !== undefined &&
      fromRoute !== null &&
      !Number.isNaN(fromRoute)
    ) {
      return fromRoute;
    }
    return loadedBatteryLevel;
  }, [location.state?.batteryLevel, loadedBatteryLevel]);

  const hasBattery =
    batteryLevel !== undefined &&
    batteryLevel !== null &&
    !Number.isNaN(batteryLevel);

  const displayLevel = hasBattery
    ? Math.min(100, Math.max(0, Math.trunc(batteryLevel)))
    : 0;

  const batteryStatusKey = useMemo(() => {
    if (!hasBattery) {
      return "installation_state_battery_status_unknown";
    }
    if (batteryLevel >= 75) {
      return "installation_state_battery_status_ok";
    }
    if (batteryLevel >= 50) {
      return "installation_state_battery_status_medium";
    }
    return "installation_state_battery_status_low";
  }, [batteryLevel, hasBattery]);

  const visual = hasBattery
    ? getBatteryVisual(batteryLevel)
    : { tone: "neutral", gaugeColor: "#94a3b8" };

  const snapshotValue = hasBattery
    ? t("installation_state_battery_subtitle_value", { percent: displayLevel })
    : t("installation_state_battery_subtitle_unknown");

  const guideItems = [
    {
      icon: FaInfoCircle,
      titleKey: "installation_state_battery_about_title",
      bodyKey: "installation_state_battery_about_body",
    },
    {
      icon: FaSun,
      titleKey: "installation_state_battery_orientation_title",
      bodyKey: "installation_state_battery_orientation_body",
    },
    {
      icon: FaChartLine,
      titleKey: "installation_state_battery_usage_title",
      bodyKey: "installation_state_battery_usage_body",
    },
  ];

  const ledItems = [
    { tone: "red", labelKey: "installation_state_battery_led_red" },
    { tone: "green", labelKey: "installation_state_battery_led_green" },
    { tone: "off", labelKey: "installation_state_battery_led_off" },
  ];

  return (
    <InstallationSubpageLayout
      pageTitle={t("installation_state_battery_page_title")}
      onBack={() => history.goBack()}
      setCookies={setCookies}
      saveUser={saveUser}
    >
      <InstallationMetricSnapshot
        icon={FaBatteryHalf}
        iconVariant="battery"
        eyebrow={t("installation_state_battery_current_title")}
        value={snapshotValue}
        valueLoading={loading}
        hint={!loading ? t(batteryStatusKey) : undefined}
        tone={visual.tone}
      />

      <section
        className={`installation-battery-target installation-battery-target-${visual.tone}`}
      >
        <div className="installation-battery-target-header">
          <span className="installation-battery-target-label">
            {t("installation_state_battery_threshold_title")}
          </span>
          <span className="installation-battery-target-value">
            {INSTALLATION_BATTERY_TARGET_PERCENT}%
          </span>
        </div>
        <div className="installation-battery-target-track" aria-hidden>
          <div
            className="installation-battery-target-fill"
            style={{ width: hasBattery ? `${displayLevel}%` : "0%" }}
          />
          <span
            className="installation-battery-target-marker"
            style={{ left: `${INSTALLATION_BATTERY_TARGET_PERCENT}%` }}
          />
        </div>
        <p className="installation-battery-target-hint">
          {t("installation_state_battery_threshold_body")}
        </p>
      </section>

      <section className="installation-battery-guide">
        {guideItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <article
              key={item.titleKey}
              className={
                index > 0
                  ? "installation-battery-guide-item installation-battery-guide-item-divided"
                  : "installation-battery-guide-item"
              }
            >
              <span className="installation-battery-guide-icon" aria-hidden>
                <Icon />
              </span>
              <div className="installation-battery-guide-body">
                <h2 className="installation-battery-guide-title">
                  {t(item.titleKey)}
                </h2>
                <p className="installation-battery-guide-text">
                  {t(item.bodyKey)}
                </p>
              </div>
            </article>
          );
        })}
      </section>

      <section className="installation-battery-led">
        <h2 className="installation-battery-led-heading">
          <FaLightbulb aria-hidden />
          {t("installation_state_battery_led_title")}
        </h2>
        <ul className="installation-battery-led-grid">
          {ledItems.map((item) => (
            <li key={item.labelKey} className="installation-battery-led-card">
              <span
                className={`installation-battery-led-dot installation-battery-led-dot-${item.tone}`}
                aria-hidden
              />
              <span className="installation-battery-led-label">
                {t(item.labelKey)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <InstallationStateSupportSection
        cameraId={id}
        emailSubjectKey="installation_state_battery_support_email_subject"
      />
    </InstallationSubpageLayout>
  );
};

export default AppInstallationBatteryDetail;
